/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Autocomplete,
  Box,
  CircularProgress,
  Paper,
  TextField,
  Theme,
  Tooltip,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { ReactElement, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import { getVendorAccount } from '../../api/vendorApi/vendorAccountsApi';
import {
  FilterTypes,
  PaginationQueryParams,
  SelectOption,
  VendorParticipationAddress,
} from '../../types';

const useStyles = makeStyles<{
  maxWidth?: string;
  autocompleteValue?: string;
  disabled?: boolean;
  minWidth?: string;
  width?: string;
}>()((theme: Theme, { maxWidth, autocompleteValue, disabled, minWidth, width }) => ({
  root: {
    ...(maxWidth && { maxWidth }),
    ...(minWidth && { minWidth }),
    ...(width && { width }),

    margin: 0,
    justifyContent: 'flex-end',
    background: disabled ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.03)',
    borderRadius: '12px',

    '@media (max-width: 550px)': {
      width: '100%',
    },

    '&:hover': {
      '& .MuiAutocomplete-clearIndicator': {
        color: theme.palette.common.white,

        '&.Mui-disabled': {
          color: theme.palette.text.disabled,
        },
      },
    },

    '& .MuiOutlinedInput-input': {
      color: theme.palette.common.white,
      padding: '12px 16px !important',
      borderRadius: '12px',

      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
        WebkitTextFillColor: theme.palette.text.disabled,
      },
    },

    '& .MuiOutlinedInput-root': {
      color: theme.palette.text.disabled,
      borderRadius: '12px',
      padding: 0,

      '&.Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderWidth: '2px',
          borderColor: theme.palette.info.main,
        },
      },

      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.secondary.dark,
        borderRadius: '12px',
      },

      '&:hover': {
        border: 'none',

        '& .MuiOutlinedInput-notchedOutline': {
          borderWidth: '2px',
          borderColor: theme.palette.info.main,
        },
      },

      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
      },
    },

    '& .MuiInputLabel-root[data-shrink="false"]': {
      color: theme.palette.text.disabled,
      top: '-3px',
    },

    '& .MuiInputLabel-root[data-shrink="true"]': {
      color: theme.palette.text.disabled,

      '&.Mui-focused': {
        color: theme.palette.common.white,
      },
    },

    '& .MuiCircularProgress-root': {
      color: theme.palette.common.white,
    },

    '& .MuiAutocomplete-clearIndicator': {
      color: theme.palette.common.white,

      '&:hover': {
        color: theme.palette.common.white,
      },

      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
      },
    },

    '& .MuiAutocomplete-popupIndicator': {
      color: autocompleteValue ? theme.palette.common.white : theme.palette.text.disabled,

      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
      },
    },
  },
  paperDropdown: {
    background: theme.palette.secondary.dark,

    '& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading, & .MuiAutocomplete-option': {
      fontSize: '14px',
      fontWeight: 500,
      fontFamily: 'Inter',
    },
  },
}));

interface FormAutocompleteFieldServerSearchProps {
  name: string;
  label: string;
  readOnly?: boolean;
  disabled?: boolean;
  maxWidth?: string;
  filters?: FilterTypes;
  optionsQuery: ({ page, size, count, match, filters }: PaginationQueryParams) => Promise<any>;
  mapQueryResults: (array: any[]) => SelectOption[];
  isOptionEqualToValue?: (option: SelectOption, value: SelectOption) => boolean;
  onValueChange?: (item: SelectOption | null) => void;
  minWidth?: string;
  width?: string;
  isDisableClearable?: boolean;
  fetchSize?: number;
  reloadKey?: string | number | null;
  minSearchChars?: number;
  searchDebounceMs?: number;
  showVendorAddressOnHover?: boolean;
}

const vendorAddressCache = new Map<string, string>();

const formatVendorAddress = (address?: VendorParticipationAddress | null) => {
  if (!address) return '';

  const cityLine = [address.city, [address.state, address.zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');

  return [address.address1, address.address2, cityLine].filter(Boolean).join(', ');
};

const VendorAddressTooltip = ({
  vendorId,
  children,
}: {
  vendorId: string;
  children: ReactElement;
}) => {
  const [address, setAddress] = useState<string | null>(vendorAddressCache.get(vendorId) ?? null);
  const [isLoading, setIsLoading] = useState(false);

  const loadAddress = () => {
    if (address !== null || isLoading) return;

    setIsLoading(true);

    getVendorAccount(Number(vendorId))
      .then((res) => {
        const formatted = formatVendorAddress(res.data.participationAddress);

        vendorAddressCache.set(vendorId, formatted);
        setAddress(formatted);
      })
      .catch(() => undefined)
      .finally(() => setIsLoading(false));
  };

  return (
    <Tooltip
      title={isLoading ? 'Loading address...' : address || 'Address not available'}
      placement="right"
      arrow
      onOpen={loadAddress}
    >
      {children}
    </Tooltip>
  );
};

const FormAutocompleteFieldServerSearch = ({
  name,
  label,
  disabled,
  maxWidth,
  readOnly,
  filters,
  optionsQuery,
  mapQueryResults,
  isOptionEqualToValue,
  onValueChange,
  minWidth,
  width,
  isDisableClearable,
  fetchSize = 100,
  reloadKey,
  minSearchChars = 3,
  searchDebounceMs = 300,
  showVendorAddressOnHover,
}: FormAutocompleteFieldServerSearchProps) => {
  const { control, watch } = useFormContext();
  const autocompleteValue = watch(name);

  const styles = useStyles({ maxWidth, autocompleteValue, disabled, minWidth, width });

  const [isLoading, setIsLoading] = useState(false);
  const [optionsData, setOptionsData] = useState<SelectOption[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isUserInput, setIsUserInput] = useState(false);
  const requestIdRef = useRef(0);
  const isInitialMount = useRef(true);

  const fetchOptions = async (matchValue: string) => {
    setIsLoading(true);
    const requestId = ++requestIdRef.current;

    try {
      const res = await optionsQuery({
        page: 1,
        size: fetchSize,
        count: true,
        match: matchValue,
        filters,
      });

      if (requestId !== requestIdRef.current) return;

      const mapped = mapQueryResults(res?.data?.resultSet || []);
      setOptionsData(mapped.sort((a, b) => a.label.localeCompare(b.label)));
    } catch (err) {
      if (requestId === requestIdRef.current) {
        console.error(err);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchOptions('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey, fetchSize]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (!isUserInput) {
      return;
    }

    const trimmedValue = inputValue.trim();

    if (!trimmedValue) {
      fetchOptions('');
      return;
    }

    if (trimmedValue.length < minSearchChars) {
      return;
    }

    const debounceTimer = setTimeout(() => {
      fetchOptions(trimmedValue);
    }, searchDebounceMs);

    return () => clearTimeout(debounceTimer);
  }, [inputValue, isUserInput, minSearchChars, searchDebounceMs]);

  const filteredOptions = optionsData.filter((option) =>
    option.label.toLowerCase().includes(inputValue.toLowerCase()),
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => (
        <Autocomplete
          value={value || null}
          inputValue={inputValue}
          onInputChange={(_, newValue, reason) => {
            setInputValue(newValue);
            setIsUserInput(reason === 'input' || reason === 'clear');
          }}
          options={filteredOptions}
          loading={isLoading}
          noOptionsText={isLoading ? 'Loading...' : 'No records available'}
          className={styles.classes.root}
          disabled={disabled}
          disableClearable={isDisableClearable}
          readOnly={readOnly}
          filterOptions={(x) => x}
          getOptionLabel={(option: SelectOption) => option.label}
          isOptionEqualToValue={
            isOptionEqualToValue ?? ((option, val) => String(option.id) === String(val.id))
          }
          onChange={(_: SyntheticEvent, newValue: SelectOption | null) => {
            onChange(newValue || { id: '', label: '' });
            onValueChange?.(newValue);
            setInputValue(newValue?.label || '');
          }}
          renderOption={(props, option) => {
            const optionContent = (
              <li {...props} key={option.id}>
                <Box display="flex" alignItems="center" gap="8px" width="100%">
                  {option.counselEntityType === 'FIRM' && <BusinessIcon fontSize="small" />}
                  {option.counselEntityType === 'VENDOR' && <StorefrontIcon fontSize="small" />}
                  <span>{option.label}</span>
                  {option.accounting?.isSynced !== undefined && (
                    <Box
                      sx={{
                        backgroundColor: option.accounting.isSynced
                          ? 'success.main'
                          : 'warning.main',
                        color: 'white',
                        fontSize: '10px',
                        padding: '2px 6px',
                        borderRadius: '8px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {option.accounting.isSynced ? 'Synced' : 'Unsynced'}
                    </Box>
                  )}
                </Box>
              </li>
            );

            if (!showVendorAddressOnHover || option.counselEntityType !== 'VENDOR') {
              return optionContent;
            }

            const vendorId = String(option.id).split(':')[1];

            return (
              <VendorAddressTooltip key={option.id} vendorId={vendorId}>
                {optionContent}
              </VendorAddressTooltip>
            );
          }}
          PaperComponent={({ children }) => (
            <Paper className={styles.classes.paperDropdown}>{children}</Paper>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    {value?.counselEntityType === 'FIRM' && (
                      <BusinessIcon fontSize="small" sx={{ ml: '6px' }} />
                    )}
                    {value?.counselEntityType === 'VENDOR' && (
                      <StorefrontIcon fontSize="small" sx={{  ml: '6px' }} />
                    )}
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}
    />
  );
};

export default FormAutocompleteFieldServerSearch;
